<script setup lang="ts">
import { computed, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Copy, Check, Sparkles } from "lucide-vue-next";
import CodeBlock from "@/components/CodeBlock.vue";
import EmbedColorPicker from "./EmbedColorPicker.vue";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";

interface Props {
    token: string;
    eventType?: { isTeamEventType?: boolean };
    embedType?: "group";
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (e: "back"): void;
}>();

const { copy, copied, isSupported } = useClipboard();
const { hasSubscription } = useHasSubscription(props.eventType);
const proDialog = useProDialog();

const selector = ref("#booking-button");
const buttonText = ref("Book a Meeting");
const buttonColor = ref("#15B79E");
const buttonTextColor = ref("#FFFFFF");

function showProModal() {
    proDialog.show({
        feature: "embed_customization",
    });
}

const code = computed(() => {
    const options: Record<string, unknown> = {
        selector: selector.value,
        type: props.embedType === "group" ? "group" : "single",
        config: { token: props.token },
    };

    if (hasSubscription) {
        options.buttonText = buttonText.value;
        options.buttonColor = buttonColor.value;
        options.buttonTextColor = buttonTextColor.value;
    }

    const optionsStr = JSON.stringify(options, null, 4);

    // Extract id/class from selector for example element
    const exampleAttr = selector.value.startsWith("#")
        ? `id="${selector.value.slice(1)}"`
        : selector.value.startsWith(".")
          ? `class="${selector.value.slice(1)}"`
          : `id="${selector.value}"`;

    return `<!-- Container where the button will be rendered -->
<div ${exampleAttr}></div>

<${"script"} src="https://iframe.zaptime.app/zaptime.js"></${"script"}>
<${"script"}>
    Zaptime.inlineButton(${optionsStr});
</${"script"}>`;
});

function copyCode() {
    copy(code.value);
}
</script>

<template>
    <div class="space-y-4">
        <button
            @click="emit('back')"
            class="gap-1 mb-2 text-sm text-iron-600 hover:text-iron-900 flex items-center"
        >
            <ArrowLeft :size="16" />
            {{ $t("event_types.share_hub.embed.back") }}
        </button>

        <div>
            <h3 class="mb-1 font-medium text-iron-900">
                {{ $t("event_types.share_hub.embed.inline_button.title") }}
            </h3>
            <p class="text-sm text-iron-500">
                {{
                    $t(
                        "event_types.share_hub.embed.inline_button.full_description",
                    )
                }}
            </p>
        </div>

        <div class="space-y-4 rounded-lg border-iron-200 bg-iron-50 p-4 border">
            <div
                class="text-xs font-medium tracking-wide text-iron-500 uppercase"
            >
                {{ $t("event_types.share_hub.embed.popup.options_title") }}
            </div>

            <div class="space-y-1">
                <Label>
                    {{
                        $t(
                            "event_types.share_hub.embed.inline_button.selector_label",
                        )
                    }}
                </Label>
                <Input v-model="selector" placeholder="#booking-button" />
                <p class="text-xs text-iron-500">
                    {{
                        $t(
                            "event_types.share_hub.embed.inline_button.selector_description",
                        )
                    }}
                </p>
            </div>

            <!-- PRO customization options - only shown for subscribed users -->
            <template v-if="hasSubscription">
                <div class="space-y-1">
                    <Label>
                        {{
                            $t(
                                "event_types.share_hub.embed.popup.button_text_label",
                            )
                        }}
                    </Label>
                    <Input v-model="buttonText" />
                </div>

                <div class="space-y-1">
                    <Label>
                        {{
                            $t(
                                "event_types.share_hub.embed.popup.button_color_label",
                            )
                        }}
                    </Label>
                    <EmbedColorPicker v-model="buttonColor" />
                </div>

                <div class="space-y-1">
                    <Label>
                        {{
                            $t(
                                "event_types.share_hub.embed.popup.text_color_label",
                            )
                        }}
                    </Label>
                    <EmbedColorPicker v-model="buttonTextColor" />
                </div>
            </template>

            <!-- PRO teaser for non-subscribed users -->
            <button
                v-else
                type="button"
                @click="showProModal"
                class="rounded-lg border-iron-300 bg-white p-3 hover:border-z-primary-300 hover:bg-z-primary-25 w-full border border-dashed text-left transition-colors"
            >
                <div class="gap-2 flex items-center">
                    <div
                        class="h-8 w-8 from-orange-400 to-pink-500 flex items-center justify-center rounded-full bg-gradient-to-br"
                    >
                        <Sparkles :size="14" class="text-white" />
                    </div>
                    <div>
                        <p class="text-sm font-medium text-iron-900">
                            {{
                                $t(
                                    "event_types.share_hub.embed.popup.pro_teaser_title",
                                )
                            }}
                        </p>
                        <p class="text-xs text-iron-500">
                            {{
                                $t(
                                    "event_types.share_hub.embed.popup.pro_teaser_description",
                                )
                            }}
                        </p>
                    </div>
                </div>
            </button>
        </div>

        <div class="rounded-lg bg-iron-50 p-4">
            <CodeBlock :code="code" />
        </div>

        <Button
            v-if="isSupported"
            variant="outline"
            @click="copyCode"
            class="gap-2"
        >
            <Check v-if="copied" :size="16" />
            <Copy v-else :size="16" />
            {{
                copied
                    ? $t("copied")
                    : $t("event_types.share_hub.embed.copy_code")
            }}
        </Button>
    </div>
</template>
